import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { enumerable } from '@Common/Core/CoreDecorator';

export class PopupParams {
    public css: Partial<CSSStyleDeclaration>;

    public className = '';
    public width: string | number = 'auto';
    public height: string | number = 'auto';

    public x: number;
    public y: number;
    public jointHeight: number;
    public padding = 5;

    public content: JQuery;

    public isModal = false;
    public isHidden = false;

    public resizable = false;
    public reusable = true;

    public minWidth = 0;
    public minHeight = 0;
    public maxWidth = Number.MAX_VALUE;
    public maxHeight = Number.MAX_VALUE;
    public resizableContent = true;
}

export class Popup {
    private events = new MulticastDelegateBag();

    private root: JQuery;

    public onClose = this.events.create<IHandler>();
    public onStopResize = this.events.create<(size: ISize) => void>();
    public onStartResize = this.events.create<(size: ISize) => void>();
    public onResize = this.events.create<(size: ISize) => void>();

    private content: JQuery;

    @enumerable
    private get body(): JQuery {
        return $('body');
    }

    constructor(private params: Partial<PopupParams> = new PopupParams()) {
        _.defaultsDeep(params, new PopupParams());
        this.content = this.params.content;

        const css = _.defaults(
            {
                visibility: 'hidden',
                width: params.width,
                height: params.height,
                position: 'absolute',
                left: null,
                top: null
            },
            params.css
        );

        this.root = $('<div class="popup-control" oncontextmenu="return false;"/>')
            .on('removePopup', () => {
                if (!this.params.isModal) {
                    if (this.params.reusable) {
                        this.root.detach();
                    } else {
                        this.root.remove();
                    }
                    this.onClose();
                }
            })
            .on(pointerHelper.pointerDownEventName, event => event.preventClearPopups())
            .css(css as any)
            .addClass(params.className);

        if (params.resizable) {
            this.root.resizable({
                minWidth: params.minWidth,
                minHeight: params.minHeight,
                maxWidth: params.maxWidth,
                maxHeight: params.maxHeight,
                start: () => {
                    this.onStartResize(this.getSize(true));
                },
                stop: () => {
                    this.onStopResize(this.getSize(true));
                },
                resize: () => {
                    this.onResize(this.getSize(true));
                }
            });
        }

        this.params.content && this.root.append(this.params.content);

        if (!this.params.isHidden) {
            this.root.appendTo(this.body);
            this.calculatePosition();
        }
    }

    private getSize(manualSizing: boolean): ISize {
        let w = manualSizing ? this.root.width() : Math.max(this.root.width(), this.params.content.width());
        let h = manualSizing ? this.root.height() : Math.max(this.root.height(), this.params.content.height());

        w = _.min([this.params.maxWidth, _.max([w, this.params.minWidth])]);
        h = _.min([this.params.maxHeight, _.max([h, this.params.minHeight])]);

        return {
            width: w,
            height: h
        };
    }

    private calculatePosition(ignoreCallback: boolean = false): void {
        if (!this.content) {
            return;
        }

        const jqWindow = $(window);
        const windowHeight = jqWindow.height();
        const windowWidth = jqWindow.width();
        const contentOuterH = this.content.outerHeight(true);
        const contentOuterW = this.content.outerWidth(true);

        if (_.isUndefined(this.params.jointHeight)) {
            this.root.css({
                top: windowHeight - this.params.y > contentOuterH ? this.params.y : windowHeight - contentOuterH,
                left: windowWidth - this.params.x > contentOuterW ? this.params.x : windowWidth - contentOuterW
            });
            this.root.css('visibility', 'visible');
            return;
        }

        let contentHeight = this.root.height() || this.content.outerHeight(true);
        const topOffset = this.params.y + this.params.jointHeight;
        const size = this.getSize(false);

        if (windowHeight - topOffset > contentHeight) {
            // Show on bottom side;
            this.root.css({
                top: topOffset,
                left: this.params.x
            });
            !ignoreCallback && this.onResize(size);
        } else if (this.params.y > contentHeight) {
            // Show on top side;
            this.root.css({
                top: this.params.y - contentHeight,
                left: this.params.x
            });
        } else if (!this.params.resizableContent && windowHeight - this.params.y < contentHeight) {
            //Show on bottom for unresizable content
            this.root.css({
                top: Math.max(windowHeight - contentHeight, this.params.padding),
                left: this.params.x
            });
        } else if (windowHeight - topOffset > this.params.y) {
            // Show on bottom side; Resize;
            this.root.css({
                top: topOffset,
                left: this.params.x
            });
            size.height = contentHeight = windowHeight - topOffset - this.params.padding;
        } else {
            // Show on top side; Resize;
            this.root.css({
                top: this.params.padding,
                left: this.params.x
            });
            if (this.params.resizableContent) {
                size.height = contentHeight = this.params.y - this.params.padding;
            }
        }

        if (!ignoreCallback) {
            this.onResize(size);
            this.calculatePosition(true);
            return;
        } else {
            const contentWidth = Math.max(this.root.width(), this.content.width());
            if (this.params.x + contentWidth > windowWidth) {
                // Do not let popup float out of bounds;
                this.root.css({
                    left: windowWidth - contentWidth - this.params.padding
                });
            }
            this.root.css({
                width: Math.round(contentWidth), // Probably should use Math.ceil?
                height: Math.round(contentHeight)
            });
        }
        this.root.css('visibility', 'visible');
    }

    public close(): void {
        this.onClose();
        this.destroy();
    }

    public getControl(): JQuery {
        return this.root;
    }

    public setContent(content: JQuery): Popup {
        this.content = this.params.content = content;
        this.root.children().detach();
        this.root.append(content);
        this.calculatePosition();
        return this;
    }

    public show(x?: number, y?: number, width?: number, height?: number): Popup {
        x != null && (this.params.x = x);
        y != null && (this.params.y = y);

        if (width) {
            this.root.width(width);
            this.content && this.content.width(width);
        }
        if (height) {
            this.root.height(height);
            this.content && this.content.height(height);
        }

        if (!this.root.elementInDOM()) {
            this.root.appendTo(this.body);
        }

        this.calculatePosition();
        return this;
    }

    public hide(): void {
        this.root && this.root.detach();
        this.onClose();
    }

    public adjust(width: number, height: number): void {
        if (width) {
            this.root.width(width);
            this.content && this.content.width(width);
        }
        if (height) {
            this.root.height(height);
            this.content && this.content.height(height);
        }
    }

    @enumerable
    public get isVisible(): boolean {
        return this.root && this.root.elementInDOM();
    }

    public setParams(newParam: Partial<PopupParams>): void {
        _.defaultsDeep(newParam, this.params);
        this.params = newParam;
    }

    public destroy(): void {
        this.params = null;
        this.root && this.root.remove();
        this.content && this.content.remove();
        this.root = null;
        this.events.destroy();
    }
}
